<template>
  <div class="dcpx">
    <main class="px-doc">
      <!-- hero -->
      <section class="px-hero">
        <span class="px-badge">Live media you can prove</span>
        <h1>
          Pricing built around what you need to
          <span class="gold">Store, Understand, and Trust</span>
        </h1>
        <p class="px-lede">
          Every Decast account includes secure casting. Choose the capacity and
          trust layer your work needs: from live to record, frame by frame,
          proof by proof.
        </p>

        <p v-if="sub" class="px-sub">
          You are on <b>{{ subName }}</b
          ><template v-if="sub.currentPeriodEnd"
            >, renews {{ fmtDate(sub.currentPeriodEnd) }}</template
          >.
          <button
            type="button"
            class="px-link"
            :disabled="!!busy"
            @click="manage"
          >
            Manage billing →
          </button>
        </p>
        <p v-if="err" class="px-err" role="alert">{{ err }}</p>

        <PlanCards
          :plans="plans"
          :topups="topups"
          :icons="icons"
          :yearly="yearly"
          :yearly-months="yearlyMonths"
          :signed-in="signedIn"
          :current-tier="currentTier"
          :busy="busy"
          :signup-path="signup"
          :contact="contact"
          free-label="Included in your account →"
          @update:yearly="yearly = $event"
          @subscribe="subscribe"
        />
      </section>

      <!-- four layers -->
      <section class="px-sec">
        <h2>What every plan is built on</h2>
        <p class="px-lede2">
          Four connected layers protect every moment of live and recorded media,
          from the first second to long after.
        </p>
        <div class="px-layers">
          <div v-for="l in layers" :key="l.name" class="px-layerc">
            <!-- the drawn marks, on the light tile they were made for -->
            <img
              v-if="layerArt(l)"
              class="px-layer-art"
              :src="layerArt(l)"
              alt=""
              loading="lazy"
            />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <svg
              v-else
              viewBox="0 0 24 24"
              aria-hidden="true"
              v-html="icons[l.icon]"
            ></svg>
            <h3>{{ l.name }}</h3>
            <p>{{ l.text }}</p>
          </div>
        </div>
      </section>

      <!-- audience -->
      <section class="px-sec px-aud">
        <div>
          <h2>
            Secure communication deserves trustworthy media infrastructure.
          </h2>
          <p class="px-lede2">
            If your work needs privacy, proof, and long-term trust, Decast is
            built for you.
          </p>
          <div class="px-chips">
            <span v-for="a in audiences" :key="a">{{ a }}</span>
          </div>
        </div>
        <ul class="px-promises">
          <li v-for="pr in promises" :key="pr">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
            {{ pr }}
          </li>
        </ul>
      </section>

      <!-- faq -->
      <section class="px-sec px-faq">
        <h2>Pricing questions</h2>
        <div v-for="(f, i) in faqs" :key="f.q" class="px-q">
          <button
            type="button"
            :aria-expanded="openFaq === i ? 'true' : 'false'"
            @click="openFaq = openFaq === i ? -1 : i"
          >
            {{ f.q }}
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              :class="{ open: openFaq === i }"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <p v-show="openFaq === i">{{ f.a }}</p>
        </div>
      </section>

      <!-- walkthrough -->
      <section class="px-walk">
        <div>
          <h2>Ask for a Decast walkthrough.</h2>
          <p>
            {{ contact }} · +351 912159105 · PCI Creative Science Park, Aveiro
            Region, Portugal
          </p>
        </div>
        <a
          class="px-cta gold"
          :href="`mailto:${contact}?subject=Decast%20walkthrough`"
          >Talk to us →</a
        >
      </section>
    </main>
  </div>
</template>

<script>
import PlanCards from './components/PlanCards.vue';
import {
  ICONS,
  PLANS,
  TOPUPS,
  LAYERS,
  AUDIENCES,
  PROMISES,
  FAQS,
  CONTACT,
  APP_SIGNUP,
  YEARLY_MONTHS,
  fromCatalog,
} from './pricingData';
import {
  fetchCatalog,
  isSignedIn,
  mySubscription,
  startCheckout,
  openPortal,
  errorMessage,
  rememberCheckout,
  PENDING_CHECKOUT_KEY,
} from '@/http/requests/billing';

export default {
  name: 'PricingPage',
  components: { PlanCards },
  data() {
    return {
      yearly: false,
      openFaq: -1,
      icons: ICONS,
      plans: PLANS,
      topups: TOPUPS,
      layers: LAYERS,
      audiences: AUDIENCES,
      promises: PROMISES,
      faqs: FAQS,
      contact: CONTACT,
      signup: APP_SIGNUP,
      yearlyMonths: YEARLY_MONTHS,
      signedIn: false,
      sub: null,
      busy: '',
      err: '',
    };
  },
  computed: {
    currentTier() {
      const p = this.sub && this.sub.plan;
      return (p && p.tier) || '';
    },
    subName() {
      const p = this.sub && this.sub.plan;
      if (!p) return this.sub ? this.sub.planId : '';
      return `${p.name.replace(/^Decast /, '')} (${
        p.interval === 'year' ? 'yearly' : 'monthly'
      })`;
    },
  },
  created() {
    document.title = 'Pricing · Decast';
    this.loadCatalog();
    this.signedIn = isSignedIn();
    if (!this.signedIn) return;
    // a checkout started signed-out resumes here after sign-in
    const pending = localStorage.getItem(PENDING_CHECKOUT_KEY);
    if (pending) {
      localStorage.removeItem(PENDING_CHECKOUT_KEY);
      this.yearly = /-year$/.test(pending);
      this.checkout(pending);
      return;
    }
    mySubscription()
      .then((s) => {
        this.sub = s;
      })
      .catch(() => {});
  },
  methods: {
    /**
     * The drawn mark for a layer, matched on the first word of its name.
     *
     * The catalogue names the layers and the artwork is named after them, so a layer the
     * catalogue adds later simply falls back to the line icon rather than breaking.
     */
    layerArt(layer) {
      const key = String(layer.name || '')
        .toLowerCase()
        .replace(/[^a-z]+.*$/, '');
      return ['information', 'identity', 'integrity', 'intelligence'].includes(key)
        ? `/pricing/four-is/${key}.png`
        : '';
    },
    // Prices and copy come from the stripe service, so the page cannot promise a price it
    // does not charge. The bundled content stays if the call fails.
    loadCatalog() {
      fetchCatalog()
        .then((c) => {
          const m = fromCatalog(c);
          if (!m) return;
          this.plans = m.plans;
          this.topups = m.topups;
          this.layers = m.layers;
          this.audiences = m.audiences;
          this.promises = m.promises;
          this.faqs = m.faqs;
          this.yearlyMonths = m.yearlyMonths;
        })
        .catch(() => {});
    },
    planIdFor(p) {
      const interval = this.yearly ? 'year' : 'month';
      const ref = p.planRefs && p.planRefs[interval];
      return (ref && ref.planId) || `${p.key}-${interval}`;
    },
    subscribe(p) {
      const planId = this.planIdFor(p);
      rememberCheckout(planId);

      if (!this.signedIn) {
        // sign up first; the plan is waiting for them on the other side
        this.$router.push(this.signup);
        return;
      }
      // signed in, so the plan page owns this: it starts the checkout, or opens billing
      // when there is already a subscription to change.
      this.$router.push(`/dashboard/account/plans?plan=${planId}`);
    },
    checkout(planId) {
      this.busy = planId.split('-')[0];
      this.err = '';
      startCheckout(planId)
        .then((session) => {
          window.location.assign(session.url);
        })
        .catch((e) => {
          const status = e && e.response && e.response.status;
          if (status === 409) {
            this.manage();
            return;
          }
          if (status === 401) {
            rememberCheckout(planId);
            this.$router.push('/v2login');
            return;
          }
          this.busy = '';
          this.err = errorMessage(
            e,
            'Checkout could not be started. Try again in a moment.'
          );
        });
    },
    manage() {
      this.busy = 'portal';
      this.err = '';
      openPortal('/pricing')
        .then((session) => {
          window.location.assign(session.url);
        })
        .catch((e) => {
          this.busy = '';
          this.err = errorMessage(
            e,
            'The billing portal is unavailable right now.'
          );
        });
    },
    fmtDate(v) {
      try {
        return new Date(v).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });
      } catch (e) {
        return '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dcpx {
  background: #0a0b0f;
  color: #fff;
  min-height: 100vh;
  padding: 40px 0 0;

  * {
    box-sizing: border-box;
    margin: 0;
  }
  ul {
    list-style: none;
    padding: 0;
  }
  a {
    text-decoration: none;
    color: inherit;
  }
  button {
    font: inherit;
    color: inherit;
    background: none;
    border: 0;
    cursor: pointer;
  }
  h1,
  h2,
  h3 {
    color: #fff;
    letter-spacing: -0.02em;
  }
  .gold {
    color: #d7df23;
  }

  .px-doc {
    max-width: 1120px;
    margin: 0 auto;
    padding: 0 24px 90px;
  }

  .px-hero {
    text-align: center;
    padding: 46px 0 34px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .px-badge {
    border: 1px solid #d7df2366;
    color: #d7df23;
    border-radius: 999px;
    padding: 4px 12px;
    font-size: 12px;
  }
  .px-hero h1 {
    max-width: 21ch;
    margin: 22px auto 0;
    font-size: clamp(1.9rem, 4vw, 3rem);
    line-height: 1.15;
  }
  .px-lede {
    max-width: 58ch;
    margin: 18px auto 0;
    color: #ffffffb3;
    font-size: 15.5px;
    line-height: 1.6;
  }

  .px-toggle {
    margin-top: 30px;
    display: inline-flex;
    align-items: center;
    gap: 12px;
    border: 1px solid #ffffff1f;
    background: #ffffff0a;
    border-radius: 999px;
    padding: 9px 18px;
    font-size: 13.5px;
    span {
      color: #ffffff8f;
    }
    span.on {
      color: #fff;
      font-weight: 600;
    }
    button {
      position: relative;
      width: 44px;
      height: 24px;
      border-radius: 999px;
      background: #ffffff33;
      transition: background 0.2s;
    }
    button.y {
      background: #d7df23;
    }
    button i {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #fff;
      transition: transform 0.2s;
    }
    button.y i {
      transform: translateX(20px);
    }
    .px-save {
      background: #d7df2326;
      color: #d7df23;
      border-radius: 999px;
      padding: 2px 9px;
      font-size: 11.5px;
      font-weight: 600;
    }
  }

  .px-sub {
    margin-top: 18px;
    font-size: 13.5px;
    color: #ffffffb3;
    b {
      color: #fff;
    }
  }
  .px-link {
    color: #d7df23;
    font-weight: 600;
    margin-left: 8px;
    &:hover {
      text-decoration: underline;
    }
    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }
  .px-err {
    margin-top: 12px;
    border: 1px solid #f0997a66;
    background: #f0997a14;
    color: #f0b8a0;
    border-radius: 10px;
    padding: 9px 14px;
    font-size: 13px;
    max-width: 60ch;
  }

  .px-plans {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 22px;
    align-items: start;
    padding-top: 14px;
  }
  .px-plan {
    position: relative;
    border: 1px solid #ffffff26;
    border-radius: 16px;
    background: #ffffff08;
    padding: 26px 24px;
    display: flex;
    flex-direction: column;
    &.pop {
      border-color: #d7df2399;
      background: #ffffff0f;
    }
  }
  .px-pop {
    position: absolute;
    top: -12px;
    left: 22px;
    background: #d7df23;
    color: #0b0c14;
    border-radius: 999px;
    padding: 2px 12px;
    font-size: 12px;
    font-weight: 700;
  }
  .px-plan-head {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .px-ic {
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    padding: 8px;
    border: 1px solid #ffffff1a;
    background: #ffffff0d;
    border-radius: 10px;
    color: #d7df23;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.7;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .px-plan-head h2 {
    font-size: 18px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .px-layer {
    color: #d7df23cc;
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    margin-top: 3px;
  }
  .px-price {
    margin-top: 18px;
    font-size: 38px;
    font-weight: 800;
    span {
      font-size: 14px;
      font-weight: 400;
      color: #ffffff8f;
    }
  }
  .px-blurb {
    margin-top: 8px;
    color: #ffffffb3;
    font-size: 13.5px;
  }
  .px-plan ul {
    margin-top: 18px;
    display: flex;
    flex-direction: column;
    gap: 9px;
  }
  .px-plan li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 13.5px;
    color: #ffffffd9;
  }
  .px-plan li svg,
  .px-promises svg {
    width: 15px;
    height: 15px;
    flex: 0 0 15px;
    margin-top: 2px;
    stroke: #d7df23;
    fill: none;
    stroke-width: 2.4;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .px-plan li em {
    font-style: normal;
    border: 1px solid #ffffff40;
    border-radius: 4px;
    padding: 0 5px;
    font-size: 9.5px;
    letter-spacing: 0.1em;
    color: #ffffff99;
    margin-left: 6px;
  }
  .px-cta {
    margin-top: 22px;
    display: block;
    text-align: center;
    border: 1px solid #ffffff4d;
    border-radius: 12px;
    padding: 12px;
    font-size: 14px;
    font-weight: 600;
    transition: opacity 0.15s;
    width: 100%;
    &:hover {
      opacity: 0.85;
    }
    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
    &.gold {
      background: #d7df23;
      border-color: #d7df23;
      color: #0b0c14;
    }
  }

  .px-pwp {
    margin-top: 20px;
    border: 1px solid #d7df234d;
    background: #d7df230d;
    border-radius: 12px;
    padding: 15px;
    .px-pwp-t {
      color: #d7df23;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }
    p {
      margin-top: 8px;
      font-size: 12.5px;
      color: #ffffffbf;
      line-height: 1.55;
    }
    a {
      display: inline-block;
      margin-top: 8px;
      color: #d7df23;
      font-size: 12.5px;
      font-weight: 600;
      &:hover {
        text-decoration: underline;
      }
    }
  }

  .px-topups {
    padding-top: 36px;
  }
  .px-k {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #ffffff80;
    margin-bottom: 12px;
  }
  .px-topup-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }
  .px-topup {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border: 1px solid #ffffff1a;
    background: #ffffff08;
    border-radius: 12px;
    padding: 13px 16px;
    font-size: 13.5px;
    span {
      display: inline-flex;
      align-items: center;
      gap: 9px;
      color: #ffffffd9;
    }
    span svg {
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      color: #d7df23;
      stroke: currentColor;
      fill: none;
      stroke-width: 1.7;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    b {
      white-space: nowrap;
    }
  }
  .px-note {
    margin-top: 14px;
    border: 1px solid #ffffff1a;
    background: #ffffff05;
    border-radius: 12px;
    padding: 12px 16px;
    font-size: 13.5px;
    color: #ffffff99;
  }

  .px-sec {
    padding-top: 64px;
    h2 {
      font-size: clamp(1.4rem, 2.6vw, 1.9rem);
    }
  }
  .px-lede2 {
    margin-top: 12px;
    max-width: 62ch;
    color: #ffffffb3;
    font-size: 14.5px;
    line-height: 1.6;
  }
  .px-layer-art {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: #f4f4f1;
    padding: 5px;
    object-fit: contain;
    display: block;
  }
  .px-layers {
    margin-top: 26px;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
  }
  .px-layerc {
    border: 1px solid #ffffff1f;
    background: #ffffff08;
    border-radius: 16px;
    padding: 22px 20px;
    > svg {
      width: 24px;
      height: 24px;
      margin-bottom: 14px;
      color: #d7df23;
      stroke: currentColor;
      fill: none;
      stroke-width: 1.7;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    h3 {
      font-size: 15px;
    }
    p {
      margin-top: 9px;
      font-size: 13px;
      color: #ffffffa6;
      line-height: 1.55;
    }
  }

  .px-aud {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    align-items: start;
  }
  .px-chips {
    margin-top: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    span {
      border: 1px solid #ffffff26;
      background: #ffffff0a;
      border-radius: 999px;
      padding: 5px 12px;
      font-size: 12px;
      color: #ffffffcc;
    }
  }
  .px-promises {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 8px;
    li {
      display: flex;
      gap: 9px;
      font-size: 14px;
      color: #ffffffd9;
    }
  }

  .px-faq {
    max-width: 760px;
  }
  .px-q {
    border-bottom: 1px solid #ffffff1a;
    button {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 14px;
      padding: 16px 0;
      font-size: 14.5px;
      font-weight: 500;
      text-align: left;
    }
    svg {
      width: 16px;
      height: 16px;
      stroke: #ffffff8f;
      fill: none;
      stroke-width: 2;
      transition: transform 0.15s;
      &.open {
        transform: rotate(180deg);
      }
    }
    p {
      padding: 0 0 16px;
      font-size: 13.5px;
      color: #ffffffb3;
      line-height: 1.6;
      max-width: 64ch;
    }
  }

  .px-walk {
    margin-top: 70px;
    border-top: 1px solid #ffffff1a;
    padding-top: 34px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
    h2 {
      font-size: 19px;
    }
    p {
      margin-top: 8px;
      font-size: 13px;
      color: #ffffff99;
    }
    .px-cta {
      margin-top: 0;
      width: auto;
      padding: 12px 26px;
    }
  }

  @media (max-width: 900px) {
    .px-plans,
    .px-topup-row {
      grid-template-columns: 1fr;
    }
    .px-layers {
      grid-template-columns: 1fr 1fr;
    }
    .px-aud {
      grid-template-columns: 1fr;
    }
  }
  @media (max-width: 560px) {
    .px-layers {
      grid-template-columns: 1fr;
    }
  }
}
</style>
